'use client'

import {
    WHATSAPP_BROADCAST_STATUS_COLORS,
    WHATSAPP_BROADCAST_STATUS_LABELS,
    WhatsappBroadcast,
    getWhatsappBroadcasts,
} from '@/lib/api/messaging/whatsapp-broadcasts-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useEffect, useState } from 'react'
import { MdAdd, MdCampaign, MdChevronRight } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const WhatsappBroadcastsPage = () => {
    const { canRead, canCreate } = useResourcePermissions('messaging', 'whatsapp_broadcasts')
    const navigate = useNavigate()
    const [broadcasts, setBroadcasts] = useState<WhatsappBroadcast[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!canRead) return
        setLoading(true)
        getWhatsappBroadcasts()
            .then(res => {
                if (res.success) setBroadcasts(res.result)
                else toast.error(res.error || 'Erro ao carregar disparos')
            })
            .catch(() => toast.error('Falha na requisição'))
            .finally(() => setLoading(false))
    }, [canRead])

    if (!canRead) {
        return (
            <div className="p-6">
                <div className="alert alert-warning">Você não tem permissão para acessar os disparos.</div>
            </div>
        )
    }

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h1 className="text-2xl font-bold">Disparo de template</h1>
                    <p className="text-sm text-base-content/70">
                        Envio de um template aprovado para uma lista de contatos, em ritmo diário controlado pelo
                        limite da Meta. Só quem responde entra no atendimento.
                    </p>
                </div>
                {canCreate && (
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm gap-2"
                        onClick={() => navigate('/messaging/whatsapp-broadcasts/new')}
                    >
                        <MdAdd size={18} /> Novo disparo
                    </button>
                )}
            </div>

            <div className="card bg-base-100 shadow-sm">
                <div className="card-body p-4">
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <span className="loading loading-spinner loading-md" />
                        </div>
                    ) : broadcasts.length === 0 ? (
                        <div className="text-center p-10 space-y-3">
                            <MdCampaign size={40} className="mx-auto text-base-content/25" />
                            <p className="text-base-content/60">Nenhum disparo criado.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-zebra table-sm">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Setor</th>
                                        <th>Template</th>
                                        <th className="text-right">Contatos</th>
                                        <th>Situação</th>
                                        <th>Criado em</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {broadcasts.map(b => (
                                        <tr
                                            key={b.id}
                                            className="cursor-pointer hover"
                                            onClick={() => navigate(`/messaging/whatsapp-broadcasts/${b.id}`)}
                                        >
                                            <td className="font-medium">{b.name}</td>
                                            <td className="text-base-content/70">{b.department?.name ?? '-'}</td>
                                            <td className="text-base-content/70">
                                                {b.template?.friendlyName ?? b.template?.name ?? '-'}
                                            </td>
                                            <td className="text-right tabular-nums">
                                                {(b._count?.recipients ?? 0).toLocaleString('pt-BR')}
                                            </td>
                                            <td>
                                                <span className={`badge badge-sm ${WHATSAPP_BROADCAST_STATUS_COLORS[b.status]}`}>
                                                    {WHATSAPP_BROADCAST_STATUS_LABELS[b.status]}
                                                </span>
                                            </td>
                                            <td className="text-base-content/70">
                                                {new Date(b.createdAt).toLocaleDateString('pt-BR')}
                                            </td>
                                            <td className="text-right">
                                                <MdChevronRight size={18} className="text-base-content/40" />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default WhatsappBroadcastsPage
